import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTheme, type Theme } from '@mui/material/styles';
import React, { useMemo, useState, type JSX, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { makeStyles } from 'tss-react/mui';

import { getReasoningPreview } from './utils/getReasoningPreview';

const THINKING_LABEL = 'Thinking';
const SHIMMER_SPREAD_PX = 36;

const getTextShimmerSx = (theme: Theme) => {
  const baseColor = theme.palette.text.tertiary;
  const highlightColor = theme.palette.text.primary;
  const shimmerBand = `linear-gradient(90deg, transparent calc(50% - ${SHIMMER_SPREAD_PX}px), ${highlightColor}, transparent calc(50% + ${SHIMMER_SPREAD_PX}px))`;
  const baseFill = `linear-gradient(${baseColor}, ${baseColor})`;

  return {
    position: 'relative' as const,
    display: 'inline-block',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere' as const,
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    backgroundImage: `${shimmerBand}, ${baseFill}`,
    backgroundSize: '250% 100%, auto',
    backgroundRepeat: 'no-repeat, no-repeat',
    backgroundPosition: '100% center, center',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    '@keyframes reasoningTextShimmer': {
      '0%': {
        backgroundPosition: '100% center, center',
      },
      '100%': {
        backgroundPosition: '0% center, center',
      },
    },
    animation: 'reasoningTextShimmer 2.5s ease-in-out infinite',
  };
};

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 1, 1, 1),
    borderRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(0.5),
  },
  reasoningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    width: 'fit-content',
    maxWidth: '100%',
    textAlign: 'left',
    borderRadius: theme.shape.borderRadius,
  },
  reasoningHeaderInteractive: {
    cursor: 'pointer',
  },
  reasoningToggleIcon: {
    flexShrink: 0,
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.tertiary,
  },
  reasoningContinuation: {
    width: '100%',
    color: theme.palette.text.tertiary,
    '& .MuiTypography-root': {
      color: theme.palette.text.tertiary,
    },
  },
}));

const createReasoningMarkdownComponents = (theme: Theme) => {
  const compactSpacing = {
    marginBottom: theme.spacing(0.25),
    '&:last-child': {
      marginBottom: 0,
    },
  };

  return {
    p: ({ children }: { children: ReactNode }) => (
      <Typography variant='paragraphRegular' component='p' color='text.tertiary' sx={compactSpacing}>
        {children}
      </Typography>
    ),
    strong: ({ children }: { children: ReactNode }) => (
      <Typography variant='paragraphMedium' component='strong' color='text.tertiary' sx={{ display: 'inline' }}>
        {children}
      </Typography>
    ),
    em: ({ children }: { children: ReactNode }) => (
      <Typography
        variant='paragraphRegular'
        component='em'
        color='text.tertiary'
        sx={{ display: 'inline', fontStyle: 'italic' }}
      >
        {children}
      </Typography>
    ),
    ul: ({ children }: { children: ReactNode }) => (
      <Box
        component='ul'
        sx={{
          ...compactSpacing,
          marginTop: 0,
          paddingLeft: theme.spacing(2),
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }: { children: ReactNode }) => (
      <Box
        component='ol'
        sx={{
          ...compactSpacing,
          marginTop: 0,
          paddingLeft: theme.spacing(2),
        }}
      >
        {children}
      </Box>
    ),
    li: ({ children }: { children: ReactNode }) => (
      <Typography
        variant='paragraphRegular'
        component='li'
        color='text.tertiary'
        sx={{ marginBottom: theme.spacing(0.25), '&:last-child': { marginBottom: 0 } }}
      >
        {children}
      </Typography>
    ),
  };
};

const getShimmerLabelText = (text: string): string => {
  const trimmed = text.trim();
  const boldMatch = /^\*\*(.+)\*\*$/s.exec(trimmed);
  if (boldMatch?.[1]) {
    return boldMatch[1].trim();
  }
  return trimmed;
};

type ShimmerLabelProps = {
  label: string;
};

const ShimmerLabel = ({ label }: ShimmerLabelProps): JSX.Element => {
  const theme = useTheme();
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  if (prefersReducedMotion) {
    return (
      <Typography variant='paragraphMedium' component='span' color='text.tertiary'>
        {label}
      </Typography>
    );
  }

  return (
    <Typography variant='paragraphMedium' component='span' sx={getTextShimmerSx(theme)}>
      {label}
    </Typography>
  );
};

type ReasoningBlockProps = {
  reasoningMessage: string | null;
};

const ReasoningBlock = ({ reasoningMessage }: ReasoningBlockProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const markdownComponents = useMemo(() => createReasoningMarkdownComponents(theme), [theme]);
  const reasoningPreview = useMemo(
    () => (reasoningMessage ? getReasoningPreview(reasoningMessage) : null),
    [reasoningMessage],
  );

  if (!reasoningMessage) {
    return (
      <Box className={classes.root}>
        <ShimmerLabel label={THINKING_LABEL} />
      </Box>
    );
  }

  const headerText = getShimmerLabelText(reasoningPreview?.preview || reasoningMessage);
  const hasOverflow = Boolean(reasoningPreview?.hasOverflow);
  const toggleLabel = isExpanded ? 'Collapse reasoning' : 'Expand reasoning';

  const headerContent = (
    <>
      <ShimmerLabel label={headerText} />
      {hasOverflow ? (
        <Box className={classes.reasoningToggleIcon} aria-hidden>
          {isExpanded ? (
            <KeyboardArrowUpRoundedIcon fontSize='small' />
          ) : (
            <KeyboardArrowDownRoundedIcon fontSize='small' />
          )}
        </Box>
      ) : null}
    </>
  );

  return (
    <Box className={classes.root}>
      {hasOverflow ? (
        <ButtonBase
          className={cx(classes.reasoningHeader, classes.reasoningHeaderInteractive)}
          onClick={() => setIsExpanded((current) => !current)}
          aria-label={toggleLabel}
          aria-expanded={isExpanded}
        >
          {headerContent}
        </ButtonBase>
      ) : (
        <Box className={classes.reasoningHeader}>{headerContent}</Box>
      )}
      {isExpanded && hasOverflow && reasoningPreview ? (
        <Box className={classes.reasoningContinuation}>
          <ReactMarkdown
            remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {reasoningPreview.continuation}
          </ReactMarkdown>
        </Box>
      ) : null}
    </Box>
  );
};

export default ReasoningBlock;
